import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { convertClassStringToProps } from '../../base/package/util/classMap';
import { isDesktopWidth } from '../../base/package/util/device';
import { cleanupRichText } from '../../base/package/util/html-helper';
import { paintTick } from '../../base/package/util/render';
import styles from './ui-rich-text.css?inline';

export const RichTextStyleType = {
  COMPONENT: 'component',
  PAGE: 'page',
} as const;

export type RichTextStyleType = (typeof RichTextStyleType)[keyof typeof RichTextStyleType];

const CName = 'ui-rich-text';

@customElement(CName)
export class UiRichText extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: String }) content: string;
  @property({ type: String }) class = '';
  @property({ type: String }) styleType: RichTextStyleType = RichTextStyleType.COMPONENT;

  @query('.wrapper') private readonly _wrapperEl?: HTMLDivElement;

  private _anchorEls: NodeListOf<HTMLAnchorElement> | undefined;
  private readonly _boundHandleShortcutLink = this._handleShortcutLink.bind(this);
  private readonly _boundHandleInternalLink = this._handleInternalLink.bind(this);
  private readonly _boundScrollToFragment = this._scrollToFragment.bind(this);

  /** Anchors that received the internal-link click handler */
  private _internalLinkEls: HTMLAnchorElement[] = [];

  private _abortController: AbortController | null = null;

  connectedCallback() {
    super.connectedCallback();
    this._abortController = new AbortController();
    window.addEventListener('hashchange', this._boundScrollToFragment, {
      signal: this._abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._abortController?.abort();
    this._abortController = null;
    this._removeShortcutLinkHandler();
    this._removeInternalLinkHandlers();
  }

  firstUpdated() {
    this.dispatchEvent(
      new CustomEvent('domReady', {
        bubbles: true,
        composed: true,
      }),
    );
    this._initShortcutLinkHandler();
    this._rewriteLinks();
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('content') && changedProperties.get('content') !== undefined) {
      // Content changed after initial render — re-initialize link handlers
      // on the new DOM nodes (unsafeHTML replaces the entire subtree).
      this._removeShortcutLinkHandler();
      this._removeInternalLinkHandlers();
      this._initShortcutLinkHandler();
      this._rewriteLinks();
    }
  }

  render() {
    const classList = classMap({
      wrapper: true,
      'component-style': this.styleType === RichTextStyleType.COMPONENT,
      'page-style': this.styleType === RichTextStyleType.PAGE,
      ...convertClassStringToProps(this.class),
    });
    let content = this.content ? cleanupRichText(this.content, 'td') : 'N/A';
    content = this._createAnchorLinks(content);
    content = this._wrapTables(content);
    content = this._processImages(content);
    this._scrollToFragment();
    return html`<div class=${classList}>${unsafeHTML(content)}</div>`;
  }

  private _removeShortcutLinkHandler() {
    this._anchorEls?.forEach((aEl) => {
      aEl.removeEventListener('click', this._boundHandleShortcutLink);
    });
    this._anchorEls = undefined;
  }

  private _handleShortcutLink(ev: Event) {
    const aEl = ev.target as HTMLAnchorElement;
    if (window.$app.router.isShortcutLink(aEl.href)) {
      ev.preventDefault();
      window.$app.router.navigateToShortcutLink(aEl.href);
    }
  }

  private _initShortcutLinkHandler() {
    if (!this._wrapperEl) {
      window.$app.logger.error('Wrapper element is not found', 'wrapperEl is undefined');
      return;
    }

    this._anchorEls = this._wrapperEl.querySelectorAll('a');
    this._anchorEls.forEach((aEl) => {
      aEl.addEventListener('click', this._boundHandleShortcutLink);
    });
  }

  /**
   * Rewrite internal links to open in the same tab.
   * Rewrite external links to open in new tab.
   * Rewrite shortcut links to be followable.
   *   from: <a href="#linkTo_terms-and-conditions">Terms</a>
   *   to: <a href="/terms-and-conditions">Terms</a>
   */
  private _rewriteLinks() {
    if (!this._wrapperEl) {
      window.$app.logger.error('Wrapper element is not found', 'wrapperEl is undefined');
      return;
    }

    const EXTERNAL_PATH_PREFIXES = ['/blog'];
    const baseOrigin = window.location.origin;

    this._anchorEls = this._wrapperEl.querySelectorAll('a');

    this._anchorEls.forEach((aEl) => {
      const href = aEl.getAttribute('href');
      if (!href) return;

      // shortcut links stay inside SPA (same tab)
      if (href.startsWith('#') && window.$app.router.isShortcutLink(href)) {
        const resolvedPath = `/${window.$app.appConfig.lang}${window.$app.router.resolveShortcutLink(href)}`;
        this._configureInternalLink(aEl, resolvedPath);
        return;
      }

      // build absolute URL (works for relative + absolute hrefs)
      let url: URL;
      try {
        url = new URL(href, baseOrigin);
      } catch {
        return; // invalid URL = ignore
      }

      const isSameOrigin = url.origin === baseOrigin;
      const isSpaInternal = isSameOrigin && !EXTERNAL_PATH_PREFIXES.some((p) => url.pathname.startsWith(p));

      if (isSpaInternal) {
        // internal game app route = same tab
        const relativePath = url.pathname + url.search + url.hash;
        // normalise to a relative path so the router intercepts
        this._configureInternalLink(aEl, relativePath);
      } else {
        // external (or allow-listed) = new tab
        aEl.setAttribute('rel', 'noopener');
        aEl.setAttribute('target', '_blank');
      }
    });
  }

  /**
   * Removes lang prefix before using our router.navigateTo
   */
  private _configureInternalLink(aEl: HTMLAnchorElement, fullPath: string) {
    aEl.removeAttribute('rel');
    aEl.removeAttribute('target');
    aEl.href = fullPath;
    aEl.dataset.internalPath = fullPath;
    aEl.addEventListener('click', this._boundHandleInternalLink);
    this._internalLinkEls.push(aEl);
  }

  // IGM-2114: Close the active modal if one is open to prevent the modal from staying open while navigating
  private _handleInternalLink(e: Event) {
    e.preventDefault();
    const aEl = e.currentTarget as HTMLAnchorElement;
    const fullPath = aEl.dataset.internalPath ?? aEl.getAttribute('href') ?? '';

    if (window.$app.modal.activeModal) {
      window.$app.modal.activeModal.modal.close();
    }

    // Navigate to the path without the language prefix
    const langPrefix = `/${window.$app.appConfig.lang}`;
    const cleanPath = fullPath.startsWith(langPrefix) ? fullPath.slice(langPrefix.length) : fullPath;
    window.$app.router.navigateTo(cleanPath);
  }

  private _removeInternalLinkHandlers() {
    this._internalLinkEls.forEach((aEl) => {
      aEl.removeEventListener('click', this._boundHandleInternalLink);
    });
    this._internalLinkEls = [];
  }

  private _wrapTables(rawHtml: string): string {
    const div = document.createElement('div');
    div.innerHTML = rawHtml;
    const tables = div.querySelectorAll('table');
    tables.forEach((table) => {
      const wrapper = document.createElement('div');
      wrapper.classList.add('table-wrapper');
      table.parentNode?.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
    return div.innerHTML;
  }

  private _processImages(rawHtml: string): string {
    const div = document.createElement('div');
    div.innerHTML = rawHtml;
    const images = div.querySelectorAll('img');
    const validTitles = ['desktop', 'mobile'];
    images.forEach((img) => {
      const title = img.getAttribute('title') ?? '';

      if (
        !validTitles.includes(title) ||
        (title === 'desktop' && !isDesktopWidth()) ||
        (title === 'mobile' && isDesktopWidth())
      ) {
        img.remove();
        return;
      }

      const src = img.getAttribute('src') ?? '';
      const alt = img.getAttribute('alt') ?? '';
      const updatedSrc = `${src}?key=seo-${title}`;

      img.setAttribute('src', updatedSrc);
      img.setAttribute('title', alt);
      img.setAttribute('decoding', 'async');
      img.setAttribute('fetchpriority', 'low');
      img.setAttribute('loading', 'lazy');
    });
    return div.innerHTML;
  }

  private _createAnchorLinks(rawHtml: string): string {
    return rawHtml.replace(/\[#(.*?)\]/g, '<span style="display:block;border:2px solid transparent;" id="$1"></span>'); // create anchor links from [#hash]
  }

  private async _scrollToFragment() {
    const signal = this._abortController?.signal;
    if (signal?.aborted) return;

    await paintTick();
    if (signal?.aborted) return;

    const fragment = window.location.hash.substring(1);
    if (fragment) {
      if (!this._wrapperEl) {
        window.$app.logger.error('Wrapper element is not found', 'wrapperEl is undefined');
        return;
      }

      const element = this._wrapperEl.querySelector(`#${fragment}`);
      if (element) {
        await paintTick();
        if (signal?.aborted) return;
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiRichText;
  }
}
